import { FinancialUnavailable, isCurrentAssessment, type FinancialResponseAvailability, type FinancialNumeric } from '../financial-unavailable';
/**
 * ICR Analysis Panel (SPRINT-6B F002)
 *
 * Displays/manages Interest Coverage Ratio (ICR) calculations for deals.
 * Shows:
 * - Summary card with current ICR ratio and status badge
 * - Trend indicator (improving/stable/declining)
 * - 3-period trend table
 * - Calculate button for backend recalculation
 * - Period selector for historical data
 *
 * States:
 * - Empty: No calculations exist, show "Calculate ICR" button
 * - Display: Show ICR summary with trend data
 * - Calculating: Loading state during calculation API call
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calculator, Loader2, TrendingUp, TrendingDown, Minus, ArrowUp, ArrowDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

// Types
interface IcrCalculation extends FinancialResponseAvailability {
  id: string;
  dealId: string;
  financialPeriodId: string | null;
  calculationDate: string;
  periodLabel: string;
  ebitda: FinancialNumeric;
  adjustedEbitda: FinancialNumeric;
  addbacks: FinancialNumeric;
  totalIncomeForServicing: FinancialNumeric;
  existingInterest: FinancialNumeric;
  proposedInterest: FinancialNumeric;
  totalInterest: FinancialNumeric;
  icrRatio: FinancialNumeric;
  icrStatus: 'strong' | 'adequate' | 'marginal' | 'weak' | null;
  lenderMinIcr: FinancialNumeric;
  meetsRequirement: boolean | null;
  headroomPercentage: FinancialNumeric;
  priorPeriodIcr: FinancialNumeric;
  priorPeriod2Icr: FinancialNumeric;
  priorPeriod3Icr: FinancialNumeric;
  yoyChangePercentage: FinancialNumeric;
  trendDirection: 'improving' | 'stable' | 'declining' | null;
  threeYearAvgIcr: FinancialNumeric;
  calculationNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CalculationResult extends FinancialResponseAvailability {
  icrRatio: FinancialNumeric;
  icrStatus: 'strong' | 'adequate' | 'marginal' | 'weak' | null;
  headroomPercentage: FinancialNumeric;
  trendDirection: 'improving' | 'stable' | 'declining' | null;
  threeYearAvgIcr: FinancialNumeric;
  meetsRequirement: boolean | null;
  lenderMinIcr: FinancialNumeric;
  breakdown: {
    adjustedEbitda: FinancialNumeric;
    addbacks: FinancialNumeric;
    totalInterest: FinancialNumeric;
  };
}

export interface IcrAnalysisPanelProps {
  dealId: string;
}

// Status badge colour mapping
const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  strong: 'default',      // Green
  adequate: 'secondary',  // Blue
  marginal: 'outline',    // Amber/warning (outline style)
  weak: 'destructive',    // Red
};

const STATUS_LABELS: Record<string, string> = {
  strong: 'Strong',
  adequate: 'Adequate',
  marginal: 'Marginal',
  weak: 'Weak',
};

// Trend icon component
const TrendIcon: React.FC<{ direction: string | null }> = ({ direction }) => {
  switch (direction) {
    case 'improving':
      return <TrendingUp className="h-4 w-4 text-green-500" />;
    case 'declining':
      return <TrendingDown className="h-4 w-4 text-red-500" />;
    case 'stable':
      return <Minus className="h-4 w-4 text-gray-500" />;
    default: return null;
  }
};

// Format currency
function formatCurrency(value: FinancialNumeric): string {
  if (value == null || !Number.isFinite(Number(value))) return 'Not assessed';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value));
}

// Format ICR ratio with 2 decimal places
function formatIcrRatio(value: FinancialNumeric): string {
  return value == null || !Number.isFinite(Number(value)) ? 'Not assessed' : Number(value).toFixed(2);
}

const PeriodIcrAnalysisPanel: React.FC<IcrAnalysisPanelProps & { selectedPeriodId: string; setSelectedPeriodId: (id: string) => void }> = ({ dealId, selectedPeriodId, setSelectedPeriodId }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [calculationResult, setCalculationResult] = useState<CalculationResult | null>(null);

  // Fetch ICR calculations for the deal
  const { data: calculations = [], isLoading, error } = useQuery<IcrCalculation[]>({
    queryKey: [`/api/crm/deals/${dealId}/icr-calculations`],
    enabled: !!dealId,
  });

  // Get most recent calculation or use calculation result
  const periodCalculations = selectedPeriodId ? calculations.filter(row => row.financialPeriodId === selectedPeriodId) : calculations;
  const latestCalculation = periodCalculations.length > 0 ? periodCalculations[periodCalculations.length - 1] : null;

  // Calculate mutation
  const calculateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', '/api/financial/calculate-icr', {
        dealId,
        periodId: selectedPeriodId || undefined,
      });
      return res.json() as Promise<CalculationResult>;
    },
    onSuccess: (data) => {
      setCalculationResult(data);
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/icr-calculations`] });
      toast({
        title: isCurrentAssessment(data) ? 'ICR calculated' : 'Not assessed',
        description: isCurrentAssessment(data) ? `Current ICR: ${formatIcrRatio(data.icrRatio)}x` : 'Review the financial evidence before assessment.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to calculate ICR',
        variant: 'destructive',
      });
    },
  });

  // Get current ICR data (from calculation result or latest record)
  const currentIcr = calculationResult || (latestCalculation ? {
    icrRatio: latestCalculation.icrRatio,
    icrStatus: latestCalculation.icrStatus,
    headroomPercentage: latestCalculation.headroomPercentage,
    trendDirection: latestCalculation.trendDirection,
    threeYearAvgIcr: latestCalculation.threeYearAvgIcr,
    meetsRequirement: latestCalculation.meetsRequirement,
    lenderMinIcr: latestCalculation.lenderMinIcr,
    breakdown: {
      adjustedEbitda: latestCalculation.adjustedEbitda,
      addbacks: latestCalculation.addbacks,
      totalInterest: latestCalculation.totalInterest,
    },
  } : null);

  // Get unique periods for selector
  const periods = calculations.map(c => ({
    id: c.financialPeriodId || c.id,
    label: c.periodLabel,
  }));
  const uniquePeriods = periods.filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i);

  // Loading state
  if (isLoading) {
    return (
      <Card data-testid="icr-analysis-panel">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i}>
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-5 w-32" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="icr-analysis-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">Interest Cover Ratio (ICR)</CardTitle>
          <div className="flex gap-2">
            {uniquePeriods.length > 0 && (
              <select
                data-testid="period-selector"
                name="periodId"
                value={selectedPeriodId}
                onChange={(e) => setSelectedPeriodId(e.target.value)}
                className="flex h-9 items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">All Periods</option>
                {uniquePeriods.map(p => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => calculateMutation.mutate()}
              disabled={calculateMutation.isPending}
            >
              {calculateMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Calculator className="h-4 w-4 mr-1" />
              )}
              Calculate ICR
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error || calculateMutation.isError || !isCurrentAssessment(calculationResult ?? latestCalculation) || currentIcr?.icrRatio == null || !Number.isFinite(Number(currentIcr.icrRatio)) ? (
          <FinancialUnavailable dealId={dealId} value={error || calculateMutation.isError ? { assessmentStatus: 'failed', historical: latestCalculation } : calculationResult ?? latestCalculation} action="Review verified financial inputs, then use Calculate ICR." />
        ) : !currentIcr ? (
          // Empty state
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">No ICR calculations for this deal</p>
            <p className="text-xs text-muted-foreground mb-6">
              Click Calculate ICR to analyse interest coverage
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary Card */}
            <div className="flex items-start justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Current ICR</p>
                <div className="flex items-center gap-2">
                  <span
                    className="text-2xl font-bold"
                    data-testid="icr-ratio-value"
                  >
                    {formatIcrRatio(currentIcr.icrRatio)}x
                  </span>
                  {currentIcr.icrStatus != null && <Badge
                    variant={STATUS_VARIANTS[currentIcr.icrStatus]}
                    data-testid="icr-status-badge"
                  >
                    {STATUS_LABELS[currentIcr.icrStatus]}
                  </Badge>}
                  <TrendIcon direction={currentIcr.trendDirection} />
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground mb-1">3-Year Avg</p>
                <span
                  className="text-lg font-medium"
                  data-testid="icr-3yr-avg"
                >
                  {currentIcr.threeYearAvgIcr == null ? 'Not assessed' : `${formatIcrRatio(currentIcr.threeYearAvgIcr)}x`}
                </span>
              </div>
            </div>

            {/* Lender Requirement & Headroom */}
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-muted-foreground">Lender Min ICR</dt>
                <dd className="font-medium">{currentIcr.lenderMinIcr == null ? 'Not assessed' : `${formatIcrRatio(currentIcr.lenderMinIcr)}x`}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Headroom</dt>
                <dd className="flex items-center gap-1" data-testid="icr-headroom">
                  {currentIcr.headroomPercentage == null ? 'Not assessed' : <>{Number(currentIcr.headroomPercentage) >= 0 ? (
                    <ArrowUp className="h-3 w-3 text-green-500" />
                  ) : (
                    <ArrowDown className="h-3 w-3 text-red-500" />
                  )}
                  <span className="font-medium">
                    {Number(currentIcr.headroomPercentage) >= 0 ? '+' : ''}
                    {formatIcrRatio(currentIcr.headroomPercentage)}% headroom
                  </span></>}
                </dd>
              </div>
            </dl>

            {/* Breakdown */}
            <div className="border-t pt-4">
              <h4 className="text-xs font-semibold text-muted-foreground mb-2">CALCULATION BREAKDOWN</h4>
              <dl className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <dt className="text-muted-foreground">Adjusted EBITDA</dt>
                  <dd className="font-medium">{formatCurrency(currentIcr.breakdown.adjustedEbitda)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Addbacks</dt>
                  <dd className="font-medium">{formatCurrency(currentIcr.breakdown.addbacks)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Total Interest</dt>
                  <dd className="font-medium">{formatCurrency(currentIcr.breakdown.totalInterest)}</dd>
                </div>
              </dl>
            </div>

            {/* Trend Table */}
            {calculations.some(isCurrentAssessment) && (
              <div className="border-t pt-4" data-testid="icr-trend-table">
                <h4 className="text-xs font-semibold text-muted-foreground mb-2">ICR TREND</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead className="text-right">EBITDA</TableHead>
                      <TableHead className="text-right">Interest</TableHead>
                      <TableHead className="text-right">ICR</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {calculations.filter(isCurrentAssessment).slice(-3).map((calc) => (
                      <TableRow key={calc.id}>
                        <TableCell className="font-medium">{calc.periodLabel}</TableCell>
                        <TableCell className="text-right">{formatCurrency(calc.adjustedEbitda)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(calc.totalInterest)}</TableCell>
                        <TableCell className="text-right">{formatIcrRatio(calc.icrRatio)}x</TableCell>
                        <TableCell>
                          {calc.icrStatus != null && <Badge variant={STATUS_VARIANTS[calc.icrStatus]} className="text-xs">
                            {STATUS_LABELS[calc.icrStatus]}
                          </Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ScopedIcrAnalysisPanel: React.FC<IcrAnalysisPanelProps> = ({ dealId }) => {
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  return <PeriodIcrAnalysisPanel key={selectedPeriodId} dealId={dealId} selectedPeriodId={selectedPeriodId} setSelectedPeriodId={setSelectedPeriodId} />;
};

export const IcrAnalysisPanel: React.FC<IcrAnalysisPanelProps> = props => <ScopedIcrAnalysisPanel key={props.dealId} {...props} />;

export default IcrAnalysisPanel;
